/**
 * Is this browser connected?
 *
 * Only the ADMIN screen uses this, and that is the point. The surveyor's screens
 * never branch on connectivity — their work goes to IndexedDB either way and the
 * sync pill reports the rest — so a hook like this in a survey screen would be a
 * defect. The admin surface is the one place that genuinely cannot function
 * offline, and it has to say so rather than fail with a spinner.
 *
 * `navigator.onLine` is famously optimistic: it reports the interface, not the
 * internet, so it is used only to make the OFFLINE case loud. A `true` here is
 * never treated as proof — every admin request still handles its own failure.
 */

import { useSyncExternalStore } from 'react';

function subscribe(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

function snapshot(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribe, snapshot, () => true);
}
